import * as React from 'react';

import {
  WAVEFORM_BAR_GAP,
  WAVEFORM_BAR_WIDTH,
  WAVEFORM_SAMPLE_MS,
  pushLevel,
  rmsLevel,
  waveformCapacity,
} from '@modules/_shared/components/chat/dictationWaveformHelpers';

type Props = {
  stream: MediaStream;
  color: string;
  baselineColor: string;
  height?: number;
};

const MIN_BAR_HEIGHT = 2;

const drawWaveform = (
  canvas: HTMLCanvasElement,
  levels: number[],
  { color, baselineColor }: { color: string; baselineColor: string },
) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  const dpr = window.devicePixelRatio || 1;
  const width = canvas.width / dpr;
  const height = canvas.height / dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);
  const capacity = waveformCapacity(width);
  const step = WAVEFORM_BAR_WIDTH + WAVEFORM_BAR_GAP;
  const offset = width - (capacity * step - WAVEFORM_BAR_GAP);
  const empty = capacity - levels.length;
  const mid = height / 2;
  for (let slot = 0; slot < capacity; slot++) {
    const x = offset + slot * step;
    const level = levels[slot - empty];
    if (level == null) {
      ctx.fillStyle = baselineColor;
      ctx.fillRect(x + WAVEFORM_BAR_WIDTH / 2 - 0.5, mid - 0.5, 1, 1);
      continue;
    }
    const barHeight = Math.max(MIN_BAR_HEIGHT, level * height);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(
      x,
      mid - barHeight / 2,
      WAVEFORM_BAR_WIDTH,
      barHeight,
      WAVEFORM_BAR_WIDTH / 2,
    );
    ctx.fill();
  }
};

export const DictationWaveform = ({
  stream,
  color,
  baselineColor,
  height = 24,
}: Props) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const colorsRef = React.useRef({ color, baselineColor });
  colorsRef.current = { color, baselineColor };

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const samples = new Uint8Array(analyser.fftSize);
    const levels: number[] = [];
    let capacity = 0;
    let frame = 0;
    let lastSample = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      capacity = waveformCapacity(rect.width);
      if (levels.length > capacity) {
        levels.splice(0, levels.length - capacity);
      }
      drawWaveform(canvas, levels, colorsRef.current);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      if (now - lastSample < WAVEFORM_SAMPLE_MS) {
        return;
      }
      lastSample = now;
      analyser.getByteTimeDomainData(samples);
      pushLevel(levels, rmsLevel(samples), capacity);
      drawWaveform(canvas, levels, colorsRef.current);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      source.disconnect();
      void audioContext.close();
    };
  }, [stream]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      css={{ display: 'block', width: '100%', height, minWidth: 0 }}
    />
  );
};
